// src/components/HotKeys.js

import { useState, useCallback } from "react";
import { ActiveSelection, Circle, Group } from "fabric";
import { useHotkeys } from "react-hotkeys-hook";
import { useData } from "../hooks/useData";
import useCanvasHistory from "../hooks/useCanvasHistory";

const HotKeys = ({
  setShowAlignDialog,
  showAlignDialog,
  setShowColorPicker,
  showColorPicker,
}) => {
  const { canvas, selectedObject, setSelectedObject, setSnackbar } = useData();
  const { saveHistory, handleUndo, handleRedo } = useCanvasHistory(canvas);
  const [clipboardObj, setClipboardObj] = useState(null);

  // Helper to check if canvas is valid
  const isCanvasValid = useCallback(() => {
    if (!canvas || !canvas.__isMounted) {
      console.error("HotKeys: Canvas is not available or not mounted");
      setSnackbar({
        open: true,
        message: "Canvas not available. Please try again.",
        severity: "error",
      });
      return false;
    }
    return true;
  }, [canvas, setSnackbar]);

  // Copy the selected object
  const handleCopyObject = useCallback(async () => {
    if (!isCanvasValid()) return;

    const activeObj = canvas.getActiveObject();
    if (!activeObj) {
      console.warn("HotKeys: No active object to copy");
      setSnackbar({
        open: true,
        message: "No object selected to copy.",
        severity: "warning",
      });
      return;
    }

    if (activeObj.type === "activeselection") {
      console.warn("HotKeys: Copying ActiveSelection is not supported");
      setSnackbar({
        open: true,
        message: "Copying multiple objects is not supported.",
        severity: "warning",
      });
      return;
    }

    try {
      let clonedObj;
      if (activeObj.type === "circle") {
        clonedObj = new Circle({
          ...activeObj.toObject([
            "id",
            "name",
            "fill",
            "stroke",
            "strokeWidth",
            "strokeDashArray",
            "strokeLineCap",
            "strokeLineJoin",
            "strokeMiterLimit",
            "left",
            "top",
            "scaleX",
            "scaleY",
            "angle",
            "opacity",
            "radius",
            "startAngle",
            "endAngle",
            "counterClockwise",
            "shadow",
            "visible",
            "backgroundColor",
            "fillRule",
            "paintFirst",
            "globalCompositeOperation",
            "skewX",
            "skewY",
            "originX",
            "originY",
          ]),
        });
      } else {
        clonedObj = await new Promise((resolve, reject) => {
          activeObj.clone(
            (cloned) => {
              if (!cloned) {
                reject(new Error("Cloning failed, cloned object is undefined"));
              } else {
                resolve(cloned);
              }
            },
            [
              "id",
              "name",
              "fill",
              "stroke",
              "strokeWidth",
              "strokeDashArray",
              "strokeLineCap",
              "strokeLineJoin",
              "strokeMiterLimit",
              "left",
              "top",
              "scaleX",
              "scaleY",
              "angle",
              "opacity",
              "shadow",
              "visible",
              "backgroundColor",
              "fillRule",
              "paintFirst",
              "globalCompositeOperation",
              "skewX",
              "skewY",
              "originX",
              "originY",
            ]
          );
        });
      }

      setClipboardObj(clonedObj);
      setSnackbar({
        open: true,
        message: "Object copied to clipboard.",
        severity: "success",
      });
      console.log("HotKeys: Copied object", { type: activeObj.type });
    } catch (error) {
      console.error("HotKeys: Error copying object", { error: error.message });
      setSnackbar({
        open: true,
        message: `Failed to copy object: ${error.message}`,
        severity: "error",
      });
    }
  }, [canvas, setSnackbar, isCanvasValid]);

  // Paste the copied object
  const handlePaste = useCallback(async () => {
    if (!isCanvasValid()) return;
    if (!clipboardObj) {
      console.warn("HotKeys: No object in clipboard to paste");
      setSnackbar({
        open: true,
        message: "No object in clipboard to paste.",
        severity: "warning",
      });
      return;
    }

    try {
      let clonedObj;
      if (clipboardObj.type === "circle") {
        clonedObj = new Circle({
          ...clipboardObj.toObject([
            "id",
            "name",
            "fill",
            "stroke",
            "strokeWidth",
            "strokeDashArray",
            "strokeLineCap",
            "strokeLineJoin",
            "strokeMiterLimit",
            "left",
            "top",
            "scaleX",
            "scaleY",
            "angle",
            "opacity",
            "radius",
            "startAngle",
            "endAngle",
            "counterClockwise",
            "shadow",
            "visible",
            "backgroundColor",
            "fillRule",
            "paintFirst",
            "globalCompositeOperation",
            "skewX",
            "skewY",
            "originX",
            "originY",
          ]),
        });
      } else {
        clonedObj = await new Promise((resolve, reject) => {
          clipboardObj.clone(
            (cloned) => {
              if (!cloned) {
                reject(new Error("Cloning failed, cloned object is undefined"));
              } else {
                resolve(cloned);
              }
            },
            [
              "id",
              "name",
              "fill",
              "stroke",
              "strokeWidth",
              "strokeDashArray",
              "strokeLineCap",
              "strokeLineJoin",
              "strokeMiterLimit",
              "left",
              "top",
              "scaleX",
              "scaleY",
              "angle",
              "opacity",
              "shadow",
              "visible",
              "backgroundColor",
              "fillRule",
              "paintFirst",
              "globalCompositeOperation",
              "skewX",
              "skewY",
              "originX",
              "originY",
            ]
          );
        });
      }

      canvas.discardActiveObject();

      const zoom = canvas.getZoom() || 1;
      const offset = 10 / zoom;
      const vpt = canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
      const newLeft = (Number(clonedObj.left || 0) + offset - vpt[4]) / zoom;
      const newTop = (Number(clonedObj.top || 0) + offset - vpt[5]) / zoom;

      clonedObj.set({
        left: newLeft,
        top: newTop,
        evented: true,
        selectable: true,
      });

      canvas.add(clonedObj);
      canvas.setActiveObject(clonedObj);
      canvas.requestRenderAll();
      saveHistory(canvas);

      setSnackbar({
        open: true,
        message: "Object pasted successfully.",
        severity: "success",
      });
      console.log("HotKeys: Pasted object", {
        type: clonedObj.type,
        left: newLeft,
        top: newTop,
      });
    } catch (error) {
      console.error("HotKeys: Error pasting object", { error: error.message });
      setSnackbar({
        open: true,
        message: `Failed to paste object: ${error.message}`,
        severity: "error",
      });
    }
  }, [canvas, clipboardObj, saveHistory, setSnackbar, isCanvasValid]);

  // Delete the selected object(s)
  const handleDelete = useCallback(() => {
    if (!isCanvasValid()) return;

    const activeObj = canvas.getActiveObject();
    if (!activeObj) {
      console.warn("HotKeys: No active object to delete");
      setSnackbar({
        open: true,
        message: "No object selected to delete.",
        severity: "warning",
      });
      return;
    }

    if (activeObj.type === "activeselection") {
      const objects = canvas.getActiveObjects();
      objects.forEach((obj) => canvas.remove(obj));
    } else {
      canvas.remove(activeObj);
    }

    canvas.discardActiveObject();
    canvas.requestRenderAll();
    setSelectedObject(null);
    saveHistory(canvas);

    setSnackbar({
      open: true,
      message: "Object(s) deleted successfully.",
      severity: "success",
    });
    console.log("HotKeys: Deleted object(s)", { type: activeObj.type });
  }, [canvas, setSelectedObject, saveHistory, setSnackbar, isCanvasValid]);

  // Nudge the selected object(s)
  const nudgeObject = useCallback(
    (direction) => {
      if (!isCanvasValid()) return;

      const activeObj = canvas.getActiveObject();
      if (!activeObj) {
        console.warn("HotKeys: No active object to nudge");
        return;
      }

      const zoom = canvas.getZoom() || 1;
      const nudgeDistance = 1 / zoom;

      if (activeObj.type === "activeselection") {
        let newLeft = activeObj.left;
        let newTop = activeObj.top;

        switch (direction) {
          case "up":
            newTop -= nudgeDistance;
            break;
          case "down":
            newTop += nudgeDistance;
            break;
          case "left":
            newLeft -= nudgeDistance;
            break;
          case "right":
            newLeft += nudgeDistance;
            break;
          default:
            return;
        }

        activeObj.set({ left: newLeft, top: newTop });
        activeObj.setCoords();
      } else {
        let newLeft = activeObj.left;
        let newTop = activeObj.top;

        switch (direction) {
          case "up":
            newTop -= nudgeDistance;
            break;
          case "down":
            newTop += nudgeDistance;
            break;
          case "left":
            newLeft -= nudgeDistance;
            break;
          case "right":
            newLeft += nudgeDistance;
            break;
          default:
            return;
        }

        activeObj.set({ left: newLeft, top: newTop });
        activeObj.setCoords();
      }

      canvas.requestRenderAll();
      setSelectedObject(activeObj);
      saveHistory(canvas);

      console.log("HotKeys: Nudged object", {
        direction,
        type: activeObj.type,
        left: activeObj.left,
        top: activeObj.top,
      });
    },
    [canvas, setSelectedObject, saveHistory, isCanvasValid]
  );

  // Duplicate the selected object (Ctrl+D)
  const handleDuplicate = useCallback(async () => {
    if (!isCanvasValid()) return;

    const activeObj = canvas.getActiveObject();
    if (!activeObj) {
      console.warn("HotKeys: No active object to duplicate");
      setSnackbar({
        open: true,
        message: "No object selected to duplicate.",
        severity: "warning",
      });
      return;
    }

    if (activeObj.type === "activeselection") {
      console.warn("HotKeys: Duplicating ActiveSelection is not supported");
      setSnackbar({
        open: true,
        message: "Duplicating multiple objects is not supported.",
        severity: "warning",
      });
      return;
    }

    await handleCopyObject();
    await handlePaste();
  }, [handleCopyObject, handlePaste, isCanvasValid]);

  // Bring Forward / Send Backward
  const adjustZIndex = useCallback(
    (direction) => {
      if (!isCanvasValid()) return;

      const activeObj = canvas.getActiveObject();
      if (!activeObj) {
        console.warn("HotKeys: No active object to adjust z-index");
        setSnackbar({
          open: true,
          message: "No object selected to adjust layering.",
          severity: "warning",
        });
        return;
      }

      if (activeObj.type === "activeselection") {
        console.warn(
          "HotKeys: Adjusting z-index for ActiveSelection is not supported"
        );
        setSnackbar({
          open: true,
          message: "Adjusting layering for multiple objects is not supported.",
          severity: "warning",
        });
        return;
      }

      if (direction === "forward") {
        canvas.bringObjectForward(activeObj, true);
      } else if (direction === "backward") {
        canvas.sendObjectBackwards(activeObj, true);
      } else if (direction === "toFront") {
        canvas.bringObjectToFront(activeObj, true);
      } else if (direction === "toBack") {
        canvas.sendObjectToBack(activeObj, true);
      }

      canvas.requestRenderAll();
      saveHistory(canvas);

      setSnackbar({
        open: true,
        message: `Object moved ${
          direction === "forward"
            ? "forward"
            : direction === "backward"
            ? "backward"
            : direction === "toFront"
            ? "to front"
            : "to back"
        }.`,
        severity: "success",
      });
      console.log("HotKeys: Adjusted z-index", {
        direction,
        type: activeObj.type,
      });
    },
    [canvas, saveHistory, setSnackbar, isCanvasValid]
  );

  // Group / Ungroup
  const handleGroup = useCallback(() => {
    if (!isCanvasValid()) return;

    const activeObj = canvas.getActiveObject();
    if (
      !activeObj ||
      activeObj.type !== "activeselection" ||
      activeObj.getObjects().length < 2
    ) {
      console.warn("HotKeys: Need at least 2 objects selected to group");
      setSnackbar({
        open: true,
        message: "Select at least 2 objects to group.",
        severity: "warning",
      });
      return;
    }

    const objects = activeObj.getObjects();
    canvas.discardActiveObject();
    const group = new Group(objects, { canvas });
    objects.forEach((obj) => canvas.remove(obj));
    canvas.add(group);
    canvas.setActiveObject(group);
    canvas.requestRenderAll();
    setSelectedObject(group);
    saveHistory(canvas);

    setSnackbar({
      open: true,
      message: "Objects grouped successfully.",
      severity: "success",
    });
    console.log("HotKeys: Grouped objects", { objectCount: objects.length });
  }, [canvas, setSelectedObject, saveHistory, setSnackbar, isCanvasValid]);

  const handleUngroup = useCallback(() => {
    if (!isCanvasValid()) return;

    const activeObj = canvas.getActiveObject();
    if (!activeObj || activeObj.type !== "group") {
      console.warn("HotKeys: Selected object is not a group");
      setSnackbar({
        open: true,
        message: "Select a group to ungroup.",
        severity: "warning",
      });
      return;
    }

    const objects = activeObj.getObjects();
    canvas.remove(activeObj);
    activeObj.destroy();
    objects.forEach((obj) => {
      canvas.add(obj);
      obj.setCoords();
    });
    const newSelection = new ActiveSelection(objects, { canvas });
    canvas.setActiveObject(newSelection);
    canvas.requestRenderAll();
    setSelectedObject(newSelection);
    saveHistory(canvas);

    setSnackbar({
      open: true,
      message: "Objects ungrouped successfully.",
      severity: "success",
    });
    console.log("HotKeys: Ungrouped objects", { objectCount: objects.length });
  }, [canvas, setSelectedObject, saveHistory, setSnackbar, isCanvasValid]);

  // Lock / Unlock
  const handleLockToggle = useCallback(
    (lock) => {
      if (!isCanvasValid()) return;

      const activeObj = canvas.getActiveObject();
      if (!activeObj) {
        console.warn("HotKeys: No active object to lock/unlock");
        setSnackbar({
          open: true,
          message: "No object selected to lock/unlock.",
          severity: "warning",
        });
        return;
      }

      const newLockState = lock;
      if (activeObj.type === "activeselection") {
        const objects = activeObj.getObjects();
        objects.forEach((obj) => {
          obj.set({
            lockMovementX: newLockState,
            lockMovementY: newLockState,
            hasControls: !newLockState,
            selectable: !newLockState,
          });
          obj.setCoords();
        });
      } else {
        activeObj.set({
          lockMovementX: newLockState,
          lockMovementY: newLockState,
          hasControls: !newLockState,
          selectable: !newLockState,
        });
        activeObj.setCoords();
      }

      canvas.discardActiveObject();
      canvas.requestRenderAll();
      setSelectedObject(null);
      saveHistory(canvas);

      setSnackbar({
        open: true,
        message: newLockState ? "Object(s) locked." : "Object(s) unlocked.",
        severity: "info",
      });
      console.log("HotKeys: Toggled lock state", {
        lockState: newLockState,
        type: activeObj.type,
      });
    },
    [canvas, setSelectedObject, saveHistory, setSnackbar, isCanvasValid]
  );

  // Zoom In / Zoom Out / Reset Zoom
  const handleZoom = useCallback(
    (action) => {
      if (!isCanvasValid()) return;

      let newZoom;
      if (action === "in") {
        newZoom = canvas.getZoom() * 1.1;
      } else if (action === "out") {
        newZoom = canvas.getZoom() / 1.1;
      } else if (action === "reset") {
        newZoom = 1;
        canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      } else {
        return;
      }

      newZoom = Math.min(Math.max(newZoom, 0.1), 10); // Limit zoom range
      canvas.setZoom(newZoom);
      canvas.requestRenderAll();
      saveHistory(canvas);

      setSnackbar({
        open: true,
        message:
          action === "reset"
            ? "Zoom reset to 100%."
            : `Zoom ${action} to ${(newZoom * 100).toFixed(0)}%.`,
        severity: "info",
      });
      console.log("HotKeys: Zoom adjusted", { action, newZoom });
    },
    [canvas, saveHistory, setSnackbar, isCanvasValid]
  );

  // Select Next / Previous Object
  const selectNextObject = useCallback(
    (direction) => {
      if (!isCanvasValid()) return;

      const objects = canvas.getObjects().filter((obj) => obj.selectable);
      if (objects.length === 0) {
        console.warn("HotKeys: No selectable objects to cycle through");
        return;
      }

      let currentIndex = -1;
      const activeObj = canvas.getActiveObject();
      if (activeObj) {
        currentIndex = objects.indexOf(activeObj);
      }

      let newIndex;
      if (direction === "next") {
        newIndex = currentIndex + 1;
        if (newIndex >= objects.length) newIndex = 0;
      } else {
        newIndex = currentIndex - 1;
        if (newIndex < 0) newIndex = objects.length - 1;
      }

      const newObj = objects[newIndex];
      canvas.setActiveObject(newObj);
      canvas.requestRenderAll();
      setSelectedObject(newObj);

      console.log("HotKeys: Selected object", {
        direction,
        type: newObj.type,
        index: newIndex,
      });
    },
    [canvas, setSelectedObject, isCanvasValid]
  );

  // Existing Hotkeys
  useHotkeys(
    "ctrl+a",
    (e) => {
      e.preventDefault();
      if (!isCanvasValid()) return;

      canvas.discardActiveObject();
      const objects = canvas.getObjects().filter((obj) => obj.selectable);
      if (objects.length > 0) {
        const sel = new ActiveSelection(objects, { canvas });
        canvas.setActiveObject(sel);
        setSelectedObject(sel);
        canvas.requestRenderAll();
        console.log("HotKeys: Selected all objects", { count: objects.length });
      } else {
        console.warn("HotKeys: No selectable objects to select");
      }
    },
    [canvas, setSelectedObject, isCanvasValid]
  );

  useHotkeys(
    "ctrl+b",
    (e) => {
      e.preventDefault();
      if (!isCanvasValid()) return;

      const activeObj = canvas.getActiveObject();
      if (!activeObj || activeObj.type !== "i-text") return;

      const newWeight = activeObj.fontWeight === "normal" ? "bold" : "normal";
      activeObj.set({ fontWeight: newWeight });
      setSelectedObject(activeObj);
      canvas.requestRenderAll();
      saveHistory(canvas);

      console.log("HotKeys: Toggled bold", { fontWeight: newWeight });
    },
    [canvas, setSelectedObject, saveHistory, isCanvasValid]
  );

  useHotkeys(
    "ctrl+c",
    (e) => {
      e.preventDefault();
      handleCopyObject();
    },
    [handleCopyObject]
  );

  useHotkeys(
    "ctrl+i",
    (e) => {
      e.preventDefault();
      if (!isCanvasValid()) return;

      const activeObj = canvas.getActiveObject();
      if (!activeObj || activeObj.type !== "i-text") return;

      const newStyle = activeObj.fontStyle === "normal" ? "italic" : "normal";
      activeObj.set({ fontStyle: newStyle });
      setSelectedObject(activeObj);
      canvas.requestRenderAll();
      saveHistory(canvas);

      console.log("HotKeys: Toggled italic", { fontStyle: newStyle });
    },
    [canvas, setSelectedObject, saveHistory, isCanvasValid]
  );

  useHotkeys(
    "ctrl+v",
    (e) => {
      e.preventDefault();
      handlePaste();
    },
    [handlePaste]
  );

  useHotkeys(
    "ctrl+x",
    (e) => {
      e.preventDefault();
      if (!isCanvasValid()) return;

      const activeObj = canvas.getActiveObject();
      if (!activeObj) {
        console.warn("HotKeys: No active object to cut");
        setSnackbar({
          open: true,
          message: "No object selected to cut.",
          severity: "warning",
        });
        return;
      }

      if (activeObj.type === "activeselection") {
        console.warn("HotKeys: Cutting ActiveSelection is not supported");
        setSnackbar({
          open: true,
          message: "Cutting multiple objects is not supported.",
          severity: "warning",
        });
        return;
      }

      handleCopyObject();
      handleDelete();
    },
    [handleCopyObject, handleDelete, isCanvasValid]
  );

  useHotkeys(
    "delete",
    (e) => {
      e.preventDefault();
      handleDelete();
    },
    [handleDelete]
  );

  useHotkeys(
    "ctrl+z",
    (e) => {
      e.preventDefault();
      handleUndo();
      console.log("HotKeys: Undo triggered");
    },
    [handleUndo]
  );

  useHotkeys(
    "ctrl+y",
    (e) => {
      e.preventDefault();
      handleRedo();
      console.log("HotKeys: Redo triggered");
    },
    [handleRedo]
  );

  useHotkeys(
    "up, arrowup",
    (e) => {
      e.preventDefault();
      nudgeObject("up");
    },
    [nudgeObject]
  );

  useHotkeys(
    "down, arrowdown",
    (e) => {
      e.preventDefault();
      nudgeObject("down");
    },
    [nudgeObject]
  );

  useHotkeys(
    "left, arrowleft",
    (e) => {
      e.preventDefault();
      nudgeObject("left");
    },
    [nudgeObject]
  );

  useHotkeys(
    "right, arrowright",
    (e) => {
      e.preventDefault();
      nudgeObject("right");
    },
    [nudgeObject]
  );

  // New Hotkeys
  useHotkeys(
    "ctrl+d",
    (e) => {
      e.preventDefault();
      handleDuplicate();
    },
    [handleDuplicate]
  );

  useHotkeys(
    "ctrl+shift+up",
    (e) => {
      e.preventDefault();
      adjustZIndex("forward");
    },
    [adjustZIndex]
  );

  useHotkeys(
    "ctrl+shift+down",
    (e) => {
      e.preventDefault();
      adjustZIndex("backward");
    },
    [adjustZIndex]
  );

  useHotkeys(
    "ctrl+shift+]",
    (e) => {
      e.preventDefault();
      adjustZIndex("toFront");
    },
    [adjustZIndex]
  );

  useHotkeys(
    "ctrl+shift+[",
    (e) => {
      e.preventDefault();
      adjustZIndex("toBack");
    },
    [adjustZIndex]
  );

  useHotkeys(
    "ctrl+g",
    (e) => {
      e.preventDefault();
      handleGroup();
    },
    [handleGroup]
  );

  useHotkeys(
    "ctrl+shift+g",
    (e) => {
      e.preventDefault();
      handleUngroup();
    },
    [handleUngroup]
  );

  useHotkeys(
    "ctrl+l",
    (e) => {
      e.preventDefault();
      handleLockToggle(true);
    },
    [handleLockToggle]
  );

  useHotkeys(
    "ctrl+shift+l",
    (e) => {
      e.preventDefault();
      handleLockToggle(false);
    },
    [handleLockToggle]
  );

  useHotkeys(
    "ctrl+shift+a",
    (e) => {
      e.preventDefault();
      if (setShowAlignDialog) {
        setShowAlignDialog(!showAlignDialog);
        console.log("HotKeys: Toggled Align Dialog", {
          showAlignDialog: !showAlignDialog,
        });
      }
    },
    [setShowAlignDialog, showAlignDialog]
  );

  useHotkeys(
    "ctrl+=",
    (e) => {
      e.preventDefault();
      handleZoom("in");
    },
    [handleZoom]
  );

  useHotkeys(
    "ctrl+-",
    (e) => {
      e.preventDefault();
      handleZoom("out");
    },
    [handleZoom]
  );

  useHotkeys(
    "ctrl+0",
    (e) => {
      e.preventDefault();
      handleZoom("reset");
    },
    [handleZoom]
  );

  useHotkeys(
    "ctrl+p",
    (e) => {
      e.preventDefault();
      if (setShowColorPicker) {
        setShowColorPicker(!showColorPicker);
        console.log("HotKeys: Toggled Color Picker", {
          showColorPicker: !showColorPicker,
        });
      }
    },
    [setShowColorPicker, showColorPicker]
  );

  useHotkeys(
    "tab",
    (e) => {
      e.preventDefault();
      selectNextObject("next");
    },
    [selectNextObject]
  );

  useHotkeys(
    "shift+tab",
    (e) => {
      e.preventDefault();
      selectNextObject("prev");
    },
    [selectNextObject]
  );

  return null;
};

export default HotKeys;
